import { useEffect, useState } from "react";
import { trainingAPI } from "@api/TrainingAPI";

// headshot path -> { v, url: Promise<objectURL|null> }. Module-scoped, so every
// useHeadshot shares one fetch per player and holds one entry per player: a new
// ?v (a re-upload) replaces the old entry and revokes its object URL.
const cache = new Map();

function getHeadshotObjectUrl(ref) {
	if (!ref) return null;
	const [path, v = ""] = ref.split("?"); // v = query string; changes iff ?v changes
	const hit = cache.get(path);
	if (hit && hit.v === v) return hit.url; // dedup: same player + same version
	if (hit) hit.url.then((u) => u && URL.revokeObjectURL(u)); // version changed → free old URL
	const url = trainingAPI
		.getHeadshotBlob(ref)
		.then((blob) => (blob ? URL.createObjectURL(blob) : null));
	cache.set(path, { v, url });
	return url;
}

// Resolves a player's headshot to an object URL from the permissioned store, or
// null (while loading, on failure, or when the player has no headshot_image_url).
// There is NO legacy fallback — the new store is the only source. A null result
// means the caller renders player initials (in an <Avatar>) or a blank placeholder
// (e.g. the <Headshot> overlay / widget silhouette) — never the legacy URL.
export function useHeadshot(player) {
	const ref = player?.headshot_image_url ?? null;
	const [objectUrl, setObjectUrl] = useState(null);

	useEffect(() => {
		if (!ref) {
			setObjectUrl(null);
			return;
		}
		let cancelled = false;
		Promise.resolve(getHeadshotObjectUrl(ref)).then((url) => {
			if (!cancelled) setObjectUrl(url);
		});
		return () => {
			cancelled = true;
		};
	}, [ref]);

	return objectUrl;
}
